import React, {useMemo, useState} from 'react';

import {useStore} from 'effector-react';

import {useTranslation} from 'react-i18next';

import {colors} from '@/app/styles/theme';
import {
    $subscriptionStatus,
    $tariffs,
    activateTrial,
    paySubscription,
} from '@/entities/subscriptions/model';
import {SubscriptionStatusEnum, SubscriptionTariff} from '@/entities/subscriptions/types';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Icon} from '@shared/ui/Icon';

import * as S from './styled';

export const PaywallPage = () => {
    const {t} = useTranslation();
    const tariffs = useStore($tariffs);
    const [activeDayIndex, setActiveDayIndex] = useState(0);
    const subscriptionStatus = useStore($subscriptionStatus);
    const dayOptions = useMemo(
        () => [
            {days: 30, label: t('subscriptions.daysSwitcher.30')},
            {days: 90, label: t('subscriptions.daysSwitcher.90')},
            {days: 180, label: t('subscriptions.daysSwitcher.180')},
            {days: 360, label: t('subscriptions.daysSwitcher.360')},
        ],
        [t],
    );
    // const selectedDays = dayOptions[activeDayIndex]?.days ?? dayOptions[0].days;

    const formattedPlans = useMemo<SubscriptionTariff[]>(
        () =>
            tariffs
                ?.filter(
                    (plan) => plan.available && plan.periodDays === dayOptions[activeDayIndex].days,
                )
                .sort((a, b) => a.amount - b.amount) ?? [],
        [tariffs, activeDayIndex],
    );

    return (
        <S.Wrapper>
            {subscriptionStatus?.status === SubscriptionStatusEnum.ACTIVE ? (
                <S.Header>
                    <S.Title>{t('subscriptions.topUpBalance')}</S.Title>
                    <S.Description>{t('subscriptions.buyMoreInvoices')}</S.Description>
                </S.Header>
            ) : (
                <S.Header>
                    <S.Title>{t('paywall.title')}</S.Title>
                    <S.Description>{t('paywall.description')}</S.Description>
                </S.Header>
            )}

            <S.DaysSwitcherSection>
                <S.DaysSwitcherContainer>
                    <S.DaysSwitcherRoot role="tablist" aria-label="Select subscription duration">
                        <S.DaysSwitcherBackground activeIndex={activeDayIndex} />
                        {dayOptions.map((option, idx) => (
                            <S.DayOptionButton
                                key={option.days}
                                type="button"
                                role="tab"
                                aria-selected={idx === activeDayIndex}
                                className={idx === activeDayIndex ? 'active' : ''}
                                onClick={() => setActiveDayIndex(idx)}
                            >
                                {option.label}
                            </S.DayOptionButton>
                        ))}
                    </S.DaysSwitcherRoot>
                </S.DaysSwitcherContainer>
            </S.DaysSwitcherSection>

            {!subscriptionStatus?.trialUsed && (
                <S.TrialCtaRow>
                    <S.TrialCtaDescription>
                        <S.TrialCtaInvoices>{t('paywall.trial.invoicesNumber')}</S.TrialCtaInvoices>
                        <S.TrialCtaText>{t('paywall.trial.description')}</S.TrialCtaText>
                    </S.TrialCtaDescription>

                    <div>
                        <Button
                            variant={ButtonVariant.Normal}
                            type="button"
                            onClick={() => {
                                activateTrial();
                            }}
                            size="md"
                        >
                            {t('paywall.button.tryFreeTrial')}
                        </Button>
                    </div>
                </S.TrialCtaRow>
            )}

            <S.PlansGrid $itemCount={formattedPlans.length}>
                {formattedPlans.map((plan) => (
                    // const isPopular = Boolean(plan.popular);

                    <S.PlanCard
                        key={plan.id}
                        role="button"
                        tabIndex={0}
                        // aria-pressed={isActive}
                        // $active={isActive}
                        // $popular={isPopular}
                        $active={plan.id === subscriptionStatus?.tariffId}
                    >
                        {/* {isPopular && <S.MostPopularBadge>Most popular</S.MostPopularBadge>} */}

                        <S.PlanHeader>
                            <S.PlanInvoices>
                                {t('paywall.daysSwitcher.invoices', {invoices: plan.invoiceLimit})}
                            </S.PlanInvoices>
                            {/* <S.PlanDescription>{plan.description}</S.PlanDescription> */}
                        </S.PlanHeader>

                        <S.PlanFeatureRow>
                            <Icon
                                name="check"
                                color={colors.white}
                                size={16}
                                style={{
                                    backgroundColor: colors.brandPrimary,
                                    borderRadius: '50%',
                                }}
                            />
                            <span>
                                {t('paywall.daysSwitcher.validFor', {days: plan.periodDays})}
                            </span>
                        </S.PlanFeatureRow>

                        <S.PlanPrice>
                            {/* <span className="price">${plan.priceMonthly}</span> */}
                            <span className="currency">{plan.currencyCode}</span>
                            <span className="price">{plan.amount}</span>
                            {/* <span className="suffix">/ month</span> */}
                        </S.PlanPrice>

                        <Button
                            variant={ButtonVariant.Normal}
                            type="button"
                            onClick={() => {
                                console.log('Get Started', plan.id);
                                paySubscription(plan.id);
                            }}
                        >
                            {t('paywall.button.getStarted')}
                        </Button>

                        <S.PlanFooter>
                            {plan.id === subscriptionStatus?.tariffId && (
                                <S.PlanFooterText>
                                    {t('paywall.button.currentPlan')}
                                </S.PlanFooterText>
                            )}
                        </S.PlanFooter>
                    </S.PlanCard>
                ))}
            </S.PlansGrid>
        </S.Wrapper>
    );
};
